"use client"

import { useEffect, useMemo, useRef, useState } from "react"

import { API_BASE_URL } from "@lib/config"
import { getSearchContext } from "@lib/search/session"
import { getClientAuthHeaders } from "@lib/auth/client-token"
import ProductCarousel from "@modules/home/components/featured-products/product-rail/product-carousel"

type PersonalizedRecommendationsPayload = {
  recentlyViewed?: any[]
  recentlyViewedTotal?: number
  latestSearches?: string[]
  searchRecommendations?: any[]
}

type PersonalizedProductSectionsProps = {
  region: any
  currentProductId?: string | null
  excludeProductIds?: Array<string | null | undefined>
  className?: string
  onStateChange?: (state: { hasData: boolean; loading: boolean }) => void
}

const EMPTY_EXCLUDED_PRODUCT_IDS: Array<string | null | undefined> = []

const normalizeObjectId = (value: unknown): string | null => {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!/^[a-fA-F0-9]{24}$/.test(trimmed)) return null
  return trimmed
}

const Section = ({
  title,
  products,
  region,
}: {
  title: string
  products: any[]
  region: any
}) => {
  if (!products.length) return null

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-start text-left">
        <span className="text-xl md:text-2xl font-lealti-light text-gray-900">
          {title}
        </span>
      </div>
      <ProductCarousel products={products} region={region} productRows={1} />
    </div>
  )
}

export default function PersonalizedProductSections({
  region,
  currentProductId,
  excludeProductIds = EMPTY_EXCLUDED_PRODUCT_IDS,
  className,
  onStateChange,
}: PersonalizedProductSectionsProps) {
  const [loading, setLoading] = useState(true)
  const [payload, setPayload] = useState<PersonalizedRecommendationsPayload | null>(null)
  const lastEmittedStateRef = useRef<{
    hasData: boolean
    loading: boolean
  } | null>(null)
  const hasNotifiedCurrentSubscriptionRef = useRef(false)

  const normalizedCurrentProductId = useMemo(
    () => normalizeObjectId(currentProductId),
    [currentProductId]
  )

  const normalizedExcludedIds = useMemo(() => {
    const values = excludeProductIds
      .map((value) => normalizeObjectId(value))
      .filter((value): value is string => Boolean(value))
    return Array.from(new Set(values))
  }, [excludeProductIds])
  const normalizedExcludedIdsKey = useMemo(
    () => normalizedExcludedIds.join(","),
    [normalizedExcludedIds]
  )

  useEffect(() => {
    const controller = new AbortController()
    const { sessionId, userToken } = getSearchContext()

    const load = async () => {
      setLoading(true)
      try {
        const url = new URL(`${API_BASE_URL}/public/recommendations/personalized`)
        if (sessionId) {
          url.searchParams.set("sessionId", sessionId)
        }
        if (userToken) {
          url.searchParams.set("userToken", userToken)
        }
        if (normalizedCurrentProductId) {
          url.searchParams.set("currentProductId", normalizedCurrentProductId)
        }
        if (normalizedExcludedIdsKey) {
          url.searchParams.set("excludeProductIds", normalizedExcludedIdsKey)
        }
        url.searchParams.set("recentLimit", "10")
        url.searchParams.set("searchLimit", "10")

        const response = await fetch(url.toString(), {
          signal: controller.signal,
          cache: "no-store",
          headers: {
            ...getClientAuthHeaders(),
          },
        })

        if (!response.ok) {
          setPayload(null)
          return
        }

        const nextPayload = (await response.json()) as PersonalizedRecommendationsPayload
        setPayload(nextPayload)
      } catch (error) {
        if ((error as any)?.name === "AbortError") return
        setPayload(null)
      } finally {
        setLoading(false)
      }
    }

    void load()

    return () => {
      controller.abort()
    }
  }, [normalizedCurrentProductId, normalizedExcludedIdsKey])

  const recentlyViewed = Array.isArray(payload?.recentlyViewed)
    ? payload!.recentlyViewed!
    : []
  const recentlyViewedTotal =
    typeof payload?.recentlyViewedTotal === "number"
      ? payload.recentlyViewedTotal
      : 0
  const searchRecommendations = Array.isArray(payload?.searchRecommendations)
    ? payload!.searchRecommendations!
    : []

  const showRecentlyViewed = recentlyViewedTotal >= 5 && recentlyViewed.length > 0
  const showSearchRecommendations = searchRecommendations.length > 0
  const hasData = showRecentlyViewed || showSearchRecommendations

  useEffect(() => {
    if (!onStateChange) {
      hasNotifiedCurrentSubscriptionRef.current = false
      return
    }

    const nextState = { hasData, loading }
    const previousState = lastEmittedStateRef.current
    const stateChanged =
      !previousState ||
      previousState.hasData !== nextState.hasData ||
      previousState.loading !== nextState.loading

    if (!hasNotifiedCurrentSubscriptionRef.current || stateChanged) {
      onStateChange(nextState)
      lastEmittedStateRef.current = nextState
      hasNotifiedCurrentSubscriptionRef.current = true
    }
  }, [hasData, loading, onStateChange])

  if (!hasData) {
    return null
  }

  return (
    <div className={className ? className : "space-y-12"}>
      {showRecentlyViewed ? (
        <Section title="Vuelve a mirar..." products={recentlyViewed} region={region} />
      ) : null}
      {showSearchRecommendations ? (
        <Section
          title="Recomendados para ti"
          products={searchRecommendations}
          region={region}
        />
      ) : null}
    </div>
  )
}
